import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Button } from "@/components/ui/button.jsx";
import { 
  Sparkles, ArrowRight, Lightbulb, Ruler, FileText, 
  Camera, Pencil, Download, CheckCircle2, FolderOpen, CalendarDays
} from 'lucide-react';

export default function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="/SNO-LightingLogo.png" 
                alt="SNO-Lighting Logo" 
                className="h-20 w-auto"
              />
            </div>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">SNO-Lighting Professional Planner</span>
            </div>
            
            <h1 className="text-4xl sm:text-6xl font-bold text-white mb-6 leading-tight">
              Plan Perfect
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Outdoor Lighting</span>
            </h1>
            
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
              Upload a photo, mark your light sections, and get a complete installation plan 
              with measurements, materials, and customer quotes.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={createPageUrl('Projects')}>
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 h-14 text-lg shadow-xl shadow-amber-500/25">
                  Start Planning
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to={createPageUrl('Projects')}>
                <Button size="lg" variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10 font-semibold px-8 h-14 text-lg">
                  <FolderOpen className="w-5 h-5 mr-2" />
                  My Projects
                </Button>
              </Link>
              <Link to={createPageUrl('Schedule')}>
                <Button size="lg" variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10 font-semibold px-8 h-14 text-lg">
                  <CalendarDays className="w-5 h-5 mr-2" />
                  Schedule
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Everything You Need</h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              From photo upload to customer quote, streamline your entire lighting installation workflow.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Camera,
                title: 'Photo Upload',
                description: 'Upload any house photo to begin your lighting plan'
              },
              {
                icon: Pencil,
                title: 'Visual Markup',
                description: 'Draw sections directly on the image with color coding'
              },
              {
                icon: Ruler,
                title: 'Auto Calculations',
                description: 'Get puck counts, wire lengths, and power requirements'
              },
              {
                icon: FileText,
                title: 'Install Sheets',
                description: 'Print-ready installer guides and customer quotes'
              }
            ].map((feature, idx) => (
              <div 
                key={idx}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-amber-500/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Workflow Section */}
      <div className="py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Simple 4-Step Process</h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Upload Photo', desc: 'Add a house photo from any angle' },
              { step: '02', title: 'Mark Sections', desc: 'Draw where lights will be installed' },
              { step: '03', title: 'Set Parameters', desc: 'Configure spacing, type, and pricing' },
              { step: '04', title: 'Generate Plan', desc: 'Get complete install docs and quote' }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                {idx < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-amber-500/50 to-transparent z-0" />
                )}
                <div className="relative z-10">
                  <div className="text-5xl font-bold text-amber-500/20 mb-4">{item.step}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What's Included Section */}
      <div className="py-24 bg-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Complete Installation Package</h2>
              <div className="space-y-4">
                {[
                  'Named Eave Sections & Measurements',
                  'Total Lighting Footage Calculations',
                  'Extension & Jump Plan',
                  'Power Distribution Plan',
                  'Material-Specific Installation Notes',
                  'Annotated Planning Image',
                  'AI Night Visualization Preview',
                  'Section Legend Table',
                  'Two-Page Installer Sheet',
                  'Professional Customer Quote'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link to={createPageUrl('Projects')} className="inline-block mt-8">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                  Create Your First Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-3xl blur-3xl" />
              <div className="relative bg-slate-900 rounded-2xl p-8 border border-slate-700">
                <div className="aspect-[4/3] bg-slate-800 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <Lightbulb className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                    <p className="text-slate-400">Your installation plan preview</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-semibold text-white">SNO-Lighting</span>
          </div>
          <p className="text-sm text-slate-500">Professional outdoor lighting installation planner</p>
        </div>
      </footer>
    </div>
  );
}